/**
 * Shared test fixtures for field definitions.
 * Exported for use in contracts, api, and webapp test files.
 */
import type {
  DocumentTypeId,
  FieldDefinitionId,
  CustomerId,
  TransitionId,
} from "../fsm.schema.ts";
import type { FieldDefinitionDto } from "./field-definitions.schema.ts";

const BASE_TIMESTAMPS = {
  createdAt: "2024-01-01T00:00:00.000Z",
  updatedAt: "2024-01-01T00:00:00.000Z",
};

const BASE_FIELD = {
  customerId: "customer-1" as CustomerId,
  documentTypeId: "dt-1" as DocumentTypeId,
  sortOrder: 0,
  isRequired: false,
  validation: {},
  options: [],
  defaultValue: null,
  translations: null,
  scopedToTransitions: [],
  ...BASE_TIMESTAMPS,
} satisfies Omit<FieldDefinitionDto, "id" | "name" | "label" | "fieldType">;

export function makeFieldDefinition(
  overrides: Partial<FieldDefinitionDto> &
    Pick<FieldDefinitionDto, "id" | "name" | "label" | "fieldType">,
): FieldDefinitionDto {
  return { ...BASE_FIELD, ...overrides };
}

// ============================================================================
// Individual field presets
// ============================================================================

export const TEXT_FIELD_WITH_VALIDATION: FieldDefinitionDto =
  makeFieldDefinition({
    id: "fd-text" as FieldDefinitionId,
    name: "code",
    label: "Code",
    fieldType: "text",
    isRequired: true,
    validation: { minLength: 2, maxLength: 10, pattern: "^[A-Z0-9]+$" },
  });

export const REQUIRED_EMAIL_FIELD: FieldDefinitionDto = makeFieldDefinition({
  id: "fd-email" as FieldDefinitionId,
  name: "email",
  label: "Email",
  fieldType: "email",
  isRequired: true,
});

export const SELECT_FIELD_WITH_OPTIONS: FieldDefinitionDto =
  makeFieldDefinition({
    id: "fd-select" as FieldDefinitionId,
    name: "priority",
    label: "Priority",
    fieldType: "select",
    isRequired: true,
    options: [
      { value: "low", label: "Low" },
      { value: "medium", label: "Medium" },
      { value: "high", label: "High" },
    ],
  });

export const NUMBER_FIELD_WITH_RANGE: FieldDefinitionDto = makeFieldDefinition({
  id: "fd-number" as FieldDefinitionId,
  name: "quantity",
  label: "Quantity",
  fieldType: "number",
  isRequired: true,
  validation: { min: 1, max: 1000 },
});

export const BOOLEAN_REQUIRED_FIELD: FieldDefinitionDto = makeFieldDefinition({
  id: "fd-bool" as FieldDefinitionId,
  name: "agreed",
  label: "Agreed",
  fieldType: "boolean",
  isRequired: true,
});

export const DATE_FIELD: FieldDefinitionDto = makeFieldDefinition({
  id: "fd-date" as FieldDefinitionId,
  name: "due_date",
  label: "Due Date",
  fieldType: "date",
  isRequired: false,
});

export const TEXTAREA_FIELD: FieldDefinitionDto = makeFieldDefinition({
  id: "fd-textarea" as FieldDefinitionId,
  name: "notes",
  label: "Notes",
  fieldType: "textarea",
  isRequired: false,
});

// ============================================================================
// Multi-field presets
// ============================================================================

export const ALL_FIELD_TYPES_SET: FieldDefinitionDto[] = [
  makeFieldDefinition({
    id: "fd-t1" as FieldDefinitionId,
    name: "title",
    label: "Title",
    fieldType: "text",
    sortOrder: 0,
  }),
  makeFieldDefinition({
    id: "fd-t2" as FieldDefinitionId,
    name: "notes",
    label: "Notes",
    fieldType: "textarea",
    sortOrder: 1,
  }),
  makeFieldDefinition({
    id: "fd-t3" as FieldDefinitionId,
    name: "contact_email",
    label: "Contact Email",
    fieldType: "email",
    sortOrder: 2,
  }),
  makeFieldDefinition({
    id: "fd-t4" as FieldDefinitionId,
    name: "count",
    label: "Count",
    fieldType: "number",
    sortOrder: 3,
  }),
  makeFieldDefinition({
    id: "fd-t5" as FieldDefinitionId,
    name: "status",
    label: "Status",
    fieldType: "select",
    sortOrder: 4,
    options: [
      { value: "open", label: "Open" },
      { value: "closed", label: "Closed" },
    ],
  }),
  makeFieldDefinition({
    id: "fd-t6" as FieldDefinitionId,
    name: "is_active",
    label: "Is Active",
    fieldType: "boolean",
    sortOrder: 5,
  }),
  makeFieldDefinition({
    id: "fd-t7" as FieldDefinitionId,
    name: "due_date",
    label: "Due Date",
    fieldType: "date",
    sortOrder: 6,
  }),
  makeFieldDefinition({
    id: "fd-t8" as FieldDefinitionId,
    name: "tracking_link",
    label: "Tracking Link",
    fieldType: "url",
    sortOrder: 7,
  }),
];

/** A valid payload satisfying ALL_FIELD_TYPES_SET with optional fields omitted */
export const VALID_PAYLOAD_FOR_ALL_TYPES: Record<string, unknown> = {
  title: "My Request",
  notes: "Some notes",
  contact_email: "user@example.com",
  count: 5,
  status: "open",
  is_active: true,
  due_date: "2025-12-31",
  tracking_link: "https://example.com/track/123",
};

// ============================================================================
// Transition-required scenario
// ============================================================================

export const TRANSITION_REQUIRED_SCENARIO = {
  /** Optional fields, one becomes required by transition */
  fieldDefinitions: [
    makeFieldDefinition({
      id: "fd-sig" as FieldDefinitionId,
      name: "signature",
      label: "Signature",
      fieldType: "text",
      isRequired: false,
    }),
    makeFieldDefinition({
      id: "fd-note" as FieldDefinitionId,
      name: "approval_note",
      label: "Approval Note",
      fieldType: "textarea",
      isRequired: false,
    }),
  ],
  /** Only signature is required for this transition */
  transitionRequiredFieldIds: ["fd-sig"],
  transitionId: "tr-1" as TransitionId,
};
